import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Link } from "react-router-dom";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";

import {
	useExecuteDeepResearchMutation,
	useGetDeepResearchPromptsQuery,
} from "@/pages/Private/redux/deepResearch/deepResearch.api";
import { useDeepResearchSocket } from "@/pages/Private/redux/deepResearch/useDeepResearchSocket";

import { ROUTE_CONFIG } from "@/routes/config";

import { copyText } from "@/utils/copyText";

import { People } from "../../../Apollo/schema/get-people";

import { RerunResearchDialog } from "./DeepResearch/RerunResearchDialog";
import { ResearchFailureNotice } from "./DeepResearch/ResearchFailureNotice";
import { ResearchRunStatus } from "./DeepResearch/ResearchRunStatus";
import { ResearchResultsView } from "./DeepResearch/ResearchResultsView";
import { formatSectionTitle, formatDeepResearchAsText } from "./deepResearchText.utils";

type DeepResearchResponse = People["deepResearchResponse"];

interface DeepResearchPanelProps {
	prospectId?: number;
	deepResearchResponse?: DeepResearchResponse;
	/** Shadow-root-safe portal target for the source popovers. */
	portalContainer?: HTMLElement | null;
	/**
	 * Opens this lead's Chat tab with the AI generate panel ready. Set by hosts
	 * that have a Chat view for the lead; a pinned fact's "In sequence" chip then
	 * leads straight into writing (PREC-3680). Omit it and the chip stays a plain
	 * unpin toggle.
	 */
	onRequestSequence?: () => void;
}

export { formatSectionTitle };

/** One deep research run costs one credit (`CreditPrices.DEEP_RESEARCH`). */
const DEEP_RESEARCH_CREDITS = 1;
const COPIED_FEEDBACK_MS = 2000;

const formatResearchDate = (dateString?: string): string | null => {
	if (!dateString) {
		return null;
	}

	const date = new Date(dateString);

	if (Number.isNaN(date.getTime())) {
		return null;
	}

	return date.toLocaleString(undefined, {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

export const DeepResearchPanel: FunctionComponent<DeepResearchPanelProps> = ({
	prospectId,
	deepResearchResponse,
	portalContainer,
	onRequestSequence,
}) => {
	const { t } = useTranslation();
	const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
	const [isRerunOpen, setIsRerunOpen] = useState(false);
	const [isCopied, setIsCopied] = useState(false);
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const { data: promptsData } = useGetDeepResearchPromptsQuery({ page: 1, limit: 100 });
	const [executeDeepResearch, { isLoading: isExecuting }] = useExecuteDeepResearchMutation();
	const { isPending, run, failure, startPending, settlePending, clearError } =
		useDeepResearchSocket(prospectId);

	const prompts = useMemo(() => promptsData?.data || [], [promptsData?.data]);
	const currentPromptId = deepResearchResponse?.deepResearchPromptId ?? null;
	const activePromptId = selectedPromptId ?? currentPromptId ?? prompts[0]?.id ?? null;
	const templateName = prompts.find((prompt) => prompt.id === currentPromptId)?.name;
	const researchedAt = formatResearchDate(deepResearchResponse?.createdAt);
	const creditLabel = t("profileTracker.deepResearch.credits", { count: DEEP_RESEARCH_CREDITS });
	const currentResponseId = deepResearchResponse?.id ?? null;

	// The result can arrive by a detail refetch instead of the socket event (the event is
	// one-shot and the extension's service worker can sleep through it). Hand whatever the
	// lead carries to the pending run so it ends when the research is new (PREC-4024).
	useEffect(() => {
		if (isPending && currentResponseId !== null) {
			settlePending(currentResponseId);
		}
	}, [isPending, currentResponseId, settlePending]);

	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) {
				clearTimeout(copyTimeoutRef.current);
			}
		};
	}, []);

	const handleCopy = useCallback(async () => {
		if (!deepResearchResponse) {
			return;
		}

		if (!(await copyText(formatDeepResearchAsText(deepResearchResponse)))) {
			return;
		}

		setIsCopied(true);

		if (copyTimeoutRef.current) {
			clearTimeout(copyTimeoutRef.current);
		}

		copyTimeoutRef.current = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
	}, [deepResearchResponse]);

	/**
	 * A run is scheduled once. `isExecuting`/`isPending` gate every entry point
	 * (the empty-state button and the re-run dialog), and the dialog closes
	 * before awaiting — a second click on a still-open dialog was how the same
	 * research got kicked off twice (PREC-3680).
	 */
	const handleRunDeepResearch = useCallback(async () => {
		if (!prospectId || isExecuting || isPending) {
			return;
		}

		setIsRerunOpen(false);

		try {
			await executeDeepResearch({
				prospectId,
				...(activePromptId ? { deepResearchPromptId: activePromptId } : {}),
			}).unwrap();
			startPending(currentResponseId);
		} catch {
			// HTTP error — the mutation's error state drives the message below.
		}
	}, [
		prospectId,
		activePromptId,
		currentResponseId,
		isExecuting,
		isPending,
		executeDeepResearch,
		startPending,
	]);

	if (!prospectId) {
		return (
			<div className="flex flex-col items-center justify-center py-12" role="alert">
				<h3 className="mb-2 text-md font-semibold text-text-meta">
					{t("profileTracker.deepResearch.noProspectTitle")}
				</h3>
				<p className="max-w-xs text-center text-sm text-text-meta/80">
					{t("profileTracker.deepResearch.noProspectDescription")}
				</p>
			</div>
		);
	}

	// A run in progress wins over an existing (older) result so a rerun shows the spinner.
	if (isPending) {
		return <ResearchRunStatus run={run} />;
	}

	// A failed run shows above the research it did not replace, and in the empty state (PREC-4052).
	const failureNotice = failure && (
		<ResearchFailureNotice
			failure={failure}
			isRetrying={isExecuting}
			onDismiss={clearError}
			onRetry={handleRunDeepResearch}
		/>
	);

	if (deepResearchResponse) {
		return (
			<div className="relative flex flex-col gap-2">
				{failureNotice}
				<ResearchResultsView
					creditLabel={creditLabel}
					isCopied={isCopied}
					isRerunDisabled={isExecuting}
					portalContainer={portalContainer}
					prospectId={prospectId}
					researchedAt={researchedAt}
					response={deepResearchResponse}
					templateName={templateName}
					onCopy={handleCopy}
					onRequestSequence={onRequestSequence}
					onRerun={() => setIsRerunOpen(true)}
				/>

				{isRerunOpen && (
					<RerunResearchDialog
						activePromptId={activePromptId}
						creditLabel={creditLabel}
						currentPromptId={currentPromptId}
						isExecuting={isExecuting}
						lastResearchedAt={researchedAt}
						prompts={prompts}
						onCancel={() => setIsRerunOpen(false)}
						onConfirm={handleRunDeepResearch}
						onPromptChange={setSelectedPromptId}
					/>
				)}
			</div>
		);
	}

	return (
		<div className="flex flex-col items-center justify-center py-12">
			<h3 className="mb-2 text-md font-semibold text-text-meta">
				{t("profileTracker.deepResearch.emptyTitle")}
			</h3>
			<p className="mb-8 max-w-xs text-center text-sm text-text-meta/80">
				{t("profileTracker.deepResearch.emptyDescription")}
			</p>

			{failureNotice && <div className="mb-4 w-full max-w-xs">{failureNotice}</div>}

			<div className="mb-6 w-full max-w-xs">
				<Button
					className="w-full"
					disabled={isExecuting}
					isLoading={isExecuting}
					size={ButtonSize.ML}
					title={t("profileTracker.deepResearch.run")}
					onClick={handleRunDeepResearch}
				/>
			</div>

			<div className="w-full max-w-xs border-t border-border-light pt-6">
				<label className="mb-2 block text-xs font-medium text-text-meta">
					{t("profileTracker.deepResearch.usingTemplate")}
				</label>
				<select
					className="input-styles w-full rounded-lg px-3 py-2 text-sm text-text"
					value={activePromptId ?? ""}
					onChange={(e) => setSelectedPromptId(Number(e.target.value))}
				>
					{prompts.map((prompt) => (
						<option key={prompt.id} value={prompt.id}>
							{prompt.name}
						</option>
					))}
				</select>
				<Link
					className="mt-3 inline-block text-xs text-blue-action hover:underline"
					to={`${ROUTE_CONFIG.TEMPLATES}?tab=DEEP_RESEARCH_PROMPTS`}
				>
					{t("profileTracker.deepResearch.editTemplate")}
				</Link>
			</div>
		</div>
	);
};
